import { useMemo, useState } from "react";
import { CalendarClock, MessageSquare, Plus } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Select";
import { Spinner } from "@/components/ui/Spinner";
import {
  CATEGORY_COLORS,
  EntryDetailModal,
  STATUS_COLORS,
} from "../components/EntryDetailModal";
import { EntryFormModal } from "../components/EntryFormModal";
import { VoteBar } from "../components/VoteBar";
import {
  useBacklogEntries,
  useCreateEntry,
  useUpdateEntry,
  useVote,
} from "../hooks/useBacklog";
import {
  allVotesLater,
  BACKLOG_CATEGORIES,
  CATEGORY_LABELS,
  STATUS_LABELS,
} from "../types";
import type { BacklogEntry, BacklogEntryFormData, BacklogVoteValue } from "../types";

function formatDate(value: string | null): string {
  if (!value) return "";
  return new Date(value).toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

interface CardProps {
  entry: BacklogEntry;
  currentUserId: string;
  onOpen: (entry: BacklogEntry) => void;
  onVote: (entry: BacklogEntry, value: BacklogVoteValue | null) => void;
}

function EntryCard({ entry, currentUserId, onOpen, onVote }: CardProps) {
  const resolved = entry.status === "done" || entry.status === "closed";
  return (
    <button
      type="button"
      onClick={() => onOpen(entry)}
      className="w-full text-left border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-4 py-3 hover:border-[var(--color-border-strong)] transition-colors duration-100"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-[var(--color-text-primary)] truncate">
            {entry.title}
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <Badge color={CATEGORY_COLORS[entry.category]}>
              {CATEGORY_LABELS[entry.category]}
            </Badge>
            {entry.is_urgent && !resolved && <Badge color="red">Pilne</Badge>}
            {entry.status !== "open" && (
              <Badge color={STATUS_COLORS[entry.status]}>{STATUS_LABELS[entry.status]}</Badge>
            )}
            {entry.discussion_date && !resolved && (
              <span className="inline-flex items-center gap-1 text-xs text-[var(--color-text-muted)]">
                <CalendarClock size={12} />
                {formatDate(entry.discussion_date)}
              </span>
            )}
            <span className="text-xs text-[var(--color-text-muted)]">
              {entry.owner_name ? `→ ${entry.owner_name}` : entry.author_name}
            </span>
            {entry.comment_count > 0 && (
              <span className="inline-flex items-center gap-1 text-xs text-[var(--color-text-muted)]">
                <MessageSquare size={12} />
                {entry.comment_count}
              </span>
            )}
          </div>
        </div>
        <VoteBar
          votes={entry.votes}
          currentUserId={currentUserId}
          onVote={resolved ? undefined : (value) => onVote(entry, value)}
        />
      </div>
    </button>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-xs font-mono font-bold uppercase tracking-wider text-[var(--color-text-muted)]">
        {title}
      </h2>
      {children}
    </section>
  );
}

export function MgmtBacklogPage() {
  const user = useAuthStore((s) => s.user);
  const { data: entries, isLoading } = useBacklogEntries();
  const createMutation = useCreateEntry();
  const updateMutation = useUpdateEntry();
  const voteMutation = useVote();

  const [categoryFilter, setCategoryFilter] = useState("");
  const [showResolved, setShowResolved] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editEntry, setEditEntry] = useState<BacklogEntry | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  const filtered = useMemo(
    () => (entries ?? []).filter((e) => !categoryFilter || e.category === categoryFilter),
    [entries, categoryFilter],
  );

  const grouped = useMemo(() => {
    const open = filtered.filter((e) => e.status === "open");
    // Agenda: urgent or dated topics, unless every cast vote says "later".
    const agenda = open
      .filter((e) => (e.is_urgent || e.discussion_date) && !allVotesLater(e))
      .sort((a, b) => {
        if (a.is_urgent !== b.is_urgent) return a.is_urgent ? -1 : 1;
        return (a.discussion_date ?? "9999") < (b.discussion_date ?? "9999") ? -1 : 1;
      });
    const agendaIds = new Set(agenda.map((e) => e.id));
    const wall = open.filter((e) => !agendaIds.has(e.id));
    const inProgress = filtered.filter((e) => e.status === "in_progress");
    const resolved = filtered.filter((e) => e.status === "done" || e.status === "closed");
    return { agenda, wall, inProgress, resolved };
  }, [filtered]);

  const detailEntry = detailId ? (entries ?? []).find((e) => e.id === detailId) ?? null : null;

  const handleFormSubmit = (data: BacklogEntryFormData) => {
    if (editEntry) {
      updateMutation.mutate(
        { id: editEntry.id, data },
        { onSuccess: () => { setFormOpen(false); setEditEntry(null); } },
      );
    } else {
      createMutation.mutate(data, { onSuccess: () => setFormOpen(false) });
    }
  };

  const currentUserId = user?.id ?? "";
  const handleVote = (entry: BacklogEntry, value: BacklogVoteValue | null) =>
    voteMutation.mutate({ id: entry.id, value });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-mono font-bold uppercase tracking-wider text-[var(--color-text-primary)]">
            Backlog zarządczy
          </h1>
          <p className="text-sm text-[var(--color-text-muted)]">
            Tematy strategiczne, kadrowe i operacyjne — do dyskusji i decyzji.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            options={[{ value: "", label: "Wszystkie kategorie" }, ...BACKLOG_CATEGORIES]}
          />
          <Button variant="primary" onClick={() => { setEditEntry(null); setFormOpen(true); }}>
            <Plus size={16} className="mr-1" />
            Dodaj temat
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Spinner />
      ) : (
        <>
          <Section title={`Na najbliższe omówienie (${grouped.agenda.length})`}>
            {grouped.agenda.length === 0 && (
              <p className="text-sm text-[var(--color-text-muted)]">
                Nic pilnego — agenda jest pusta.
              </p>
            )}
            {grouped.agenda.map((e) => (
              <EntryCard
                key={e.id}
                entry={e}
                currentUserId={currentUserId}
                onOpen={(entry) => setDetailId(entry.id)}
                onVote={handleVote}
              />
            ))}
          </Section>

          <Section title={`Ściana pomysłów (${grouped.wall.length})`}>
            {grouped.wall.length === 0 && (
              <p className="text-sm text-[var(--color-text-muted)]">Pusto na ścianie.</p>
            )}
            {grouped.wall.map((e) => (
              <EntryCard
                key={e.id}
                entry={e}
                currentUserId={currentUserId}
                onOpen={(entry) => setDetailId(entry.id)}
                onVote={handleVote}
              />
            ))}
          </Section>

          {grouped.inProgress.length > 0 && (
            <Section title={`W realizacji (${grouped.inProgress.length})`}>
              {grouped.inProgress.map((e) => (
                <EntryCard
                  key={e.id}
                  entry={e}
                  currentUserId={currentUserId}
                  onOpen={(entry) => setDetailId(entry.id)}
                  onVote={handleVote}
                />
              ))}
            </Section>
          )}

          <div>
            <Button variant="ghost" size="sm" onClick={() => setShowResolved(!showResolved)}>
              {showResolved ? "Ukryj rozstrzygnięte" : `Rozstrzygnięte (${grouped.resolved.length})`}
            </Button>
          </div>
          {showResolved && (
            <Section title="Rozstrzygnięte">
              {grouped.resolved.map((e) => (
                <EntryCard
                  key={e.id}
                  entry={e}
                  currentUserId={currentUserId}
                  onOpen={(entry) => setDetailId(entry.id)}
                  onVote={handleVote}
                />
              ))}
            </Section>
          )}
        </>
      )}

      <EntryFormModal
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditEntry(null); }}
        onSubmit={handleFormSubmit}
        submitting={createMutation.isPending || updateMutation.isPending}
        entry={editEntry}
      />

      {detailEntry && (
        <EntryDetailModal
          entry={detailEntry}
          onClose={() => setDetailId(null)}
          onEdit={(entry) => { setEditEntry(entry); setFormOpen(true); }}
        />
      )}
    </div>
  );
}
